import { useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactElement, RefObject } from 'react';

import { AppIconComponents } from '@/config/appIcons';
import { LottieAssets } from '@/config/lottieAssets';
import { seekLottie, useAnimationController, useLottie } from '@/utils/useLottie';

import { SpeechLottie } from './SpeechLottie';
import styles from './MicButton.module.css';

/**
 * Перенос `presentation/widgets/mic_button/mic_button.dart`.
 *
 * Главная кнопка экрана чата: под ней бесконечно ходит Lottie `Voice.json`,
 * а в центре кросс-фейдом сменяются три состояния (спиннер перевода,
 * анимация речи, иконка микрофона).
 */

/** `_defaultDuration = Duration(seconds: 10)`. */
const DEFAULT_DURATION_MS = 10_000;

/**
 * `_holdingDuration = Duration(seconds: (_defaultDuration.inSeconds / 10).round())`
 * — во время записи анимация идёт в десять раз быстрее.
 */
const HOLDING_DURATION_MS = Math.round(DEFAULT_DURATION_MS / 1000 / 10) * 1000;

/** `AnimatedSwitcher(duration: Duration(milliseconds: 400))`. */
const SWITCH_DURATION_MS = 400;

const MicIcon = AppIconComponents.mic;

/** `Size` из Flutter: пиксели уже посчитаны вызывающим (в Dart `Size(200.w, 200.w)`). */
export interface MicButtonSize {
  readonly width: number;
  readonly height: number;
}

export interface MicButtonProps {
  readonly size: MicButtonSize;
  readonly isRecording: boolean;
  readonly isLoading: boolean;
  readonly isSpeeking: boolean;
  readonly onStart?: () => void;
  readonly onEnd?: () => void;
}

/** Ключи детей `AnimatedSwitcher` — те же `ValueKey`, что в Dart. */
type MicCenterKey = 'translating' | 'speeking' | 'none';

function centerKeyOf(isLoading: boolean, isSpeeking: boolean): MicCenterKey {
  if (isLoading && !isSpeeking) return 'translating';
  if (isSpeeking) return 'speeking';
  return 'none';
}

/**
 * `_controller` из `_MicButtonState`: гоняет прогресс `Voice.json` туда-обратно.
 *
 * `didUpdateWidget` во Flutter останавливал контроллер и перезапускал его в ту
 * же сторону при смене `isRecording`; здесь то же самое делает эффект
 * [useAnimationController] — он перезапускается, когда меняется `durationMs`.
 */
function useVoiceLottie(isRecording: boolean): RefObject<HTMLDivElement | null> {
  const [forward, setForward] = useState(true);
  const { containerRef, animationRef } = useLottie({
    animationData: LottieAssets.voice,
  });

  useAnimationController({
    forward,
    durationMs: isRecording ? HOLDING_DURATION_MS : DEFAULT_DURATION_MS,
    // `_onAnimationStatusChanges`: дошли до края — идём обратно.
    onCompleted: () => setForward((value) => !value),
    onTick: (progress) => seekLottie(animationRef.current, progress),
  });

  return containerRef;
}

/**
 * `CircularProgressIndicator(color: Colors.white, strokeWidth: 4.w)`.
 *
 * Material рисует бесконечную дугу, которая одновременно крутится и меняет
 * длину. В вебе это две CSS-анимации: поворот всего круга и «дыхание»
 * пунктира. `viewBox` в 100 единиц на коробку в `100.w` делает толщину
 * линии ровно `4.w`, а радиус 48 повторяет вписанную дугу Flutter
 * (`strokeAlign` по центру — прямоугольник сжат на половину толщины).
 */
function MicSpinner(): ReactElement {
  return (
    <div className={styles.spinner}>
      <svg className={styles.spinnerRing} viewBox="0 0 100 100">
        <circle className={styles.spinnerArc} cx="50" cy="50" r="48" />
      </svg>
    </div>
  );
}

function renderCenterContent(key: MicCenterKey): ReactElement {
  switch (key) {
    case 'translating':
      return <MicSpinner />;
    case 'speeking':
      return <SpeechLottie />;
    case 'none':
      return <MicIcon className={styles.micIcon} />;
  }
}

interface SwitcherState {
  /** Что сейчас на экране: уходящие варианты идут первыми, текущий — последним. */
  readonly keys: readonly MicCenterKey[];
  /** Первый ребёнок `AnimatedSwitcher` появляется без анимации. */
  readonly animated: boolean;
}

/**
 * Кросс-фейд `AnimatedSwitcher`: уходящий ребёнок остаётся в дереве, пока
 * не догорит его анимация, и всё это время лежит под новым.
 *
 * `useLayoutEffect`, а не `useEffect`: новый вариант должен появиться в том же
 * кадре, где старый начал гаснуть, иначе между ними мигает пустой центр.
 */
function useAnimatedSwitcher(activeKey: MicCenterKey): SwitcherState {
  const initialKeyRef = useRef(activeKey);
  const [keys, setKeys] = useState<readonly MicCenterKey[]>(() => [activeKey]);
  const [animated, setAnimated] = useState(false);

  useLayoutEffect(() => {
    if (!animated && activeKey === initialKeyRef.current) return undefined;

    setAnimated(true);
    setKeys((prev) => (prev.includes(activeKey) ? prev : [...prev, activeKey]));

    const timer = window.setTimeout(() => {
      setKeys((prev) => {
        const next = prev.filter((key) => key === activeKey);
        return next.length === prev.length ? prev : next;
      });
    }, SWITCH_DURATION_MS);

    return () => window.clearTimeout(timer);
  }, [activeKey, animated]);

  return { keys, animated };
}

function itemClassName(isActive: boolean, animated: boolean): string {
  if (!isActive) return `${styles.item} ${styles.itemLeaving}`;
  return animated ? `${styles.item} ${styles.itemEntering}` : styles.item;
}

interface MicCenterProps {
  readonly activeKey: MicCenterKey;
}

/** `IgnorePointer(Center(AnimatedSwitcher(...)))`. */
function MicCenter({ activeKey }: MicCenterProps): ReactElement {
  const { keys, animated } = useAnimatedSwitcher(activeKey);
  return (
    <div
      className={styles.overlay}
      style={{ '--mic-switch-duration': `${SWITCH_DURATION_MS}ms` } as CSSProperties}
    >
      {keys.map((key) => (
        <div key={key} className={itemClassName(key === activeKey, animated)}>
          {renderCenterContent(key)}
        </div>
      ))}
    </div>
  );
}

export function MicButton({
  size,
  isRecording,
  isLoading,
  isSpeeking,
  onStart,
  onEnd,
}: MicButtonProps): ReactElement {
  const containerRef = useVoiceLottie(isRecording);
  const pressedRef = useRef(false);

  /** `_canRecord` */
  const canRecord = !(isSpeeking || isLoading);

  const handlePointerDown = (): void => {
    if (!canRecord) return;
    pressedRef.current = true;
    onStart?.();
  };

  /*
    Во Flutter отпускание приходило только как `onPointerUp`. В браузере
    указатель может пропасть иначе: система перехватывает жест
    (`pointercancel`) или курсор уходит за пределы кнопки — без этих двух
    случаев запись осталась бы висеть включённой. `pressedRef` не даёт
    позвать `onEnd` дважды, если после ухода курсора придёт и `pointerup`.
  */
  const handleRelease = (): void => {
    if (!pressedRef.current) return;
    pressedRef.current = false;
    if (!canRecord) return;
    onEnd?.();
  };

  return (
    <div className={styles.center}>
      <div
        className={styles.button}
        style={{ width: `${size.width}px`, height: `${size.height}px` }}
        onPointerDown={handlePointerDown}
        onPointerUp={handleRelease}
        onPointerCancel={handleRelease}
        onPointerLeave={handleRelease}
      >
        <div className={styles.lottieBox}>
          <div ref={containerRef} className={styles.lottie} />
        </div>
        <MicCenter activeKey={centerKeyOf(isLoading, isSpeeking)} />
      </div>
    </div>
  );
}
